/**
 * MessageContent
 *
 * Renders LLM-generated Markdown from the DB in WhatsApp visual style.
 *
 * The LLM only produces Markdown that maps cleanly to WhatsApp formatting:
 *   **bold**        → bold text
 *   _italic_        → italic text
 *   - / * item      → • bullet with hanging indent
 *   1. item         → numbered list with hanging indent
 *   # Heading       → bold (WhatsApp has no headings)
 *   > blockquote    → italic (WhatsApp has no blockquote)
 *   [text](url)     → URL text only (WhatsApp strips link labels)
 *   ---             → removed (WhatsApp strips horizontal rules)
 *   Emojis          → Apple CDN images
 */

import React from "react";
import ReactMarkdown from "react-markdown";
import { EMOJI_CDN_BASE_URL } from "../../constants/baseURLs";

// Helper: Converts Unicode emoji character(s) to jsDelivr-compatible hex format.
const emojiToHex = (emoji: string): string => {
  return Array.from(emoji)
    .map((c) => c.codePointAt(0)?.toString(16))
    .join("-");
};

// Converts emoji characters in a plain string to Apple CDN <img> nodes.
function withAppleEmojis(text: string): React.ReactNode[] {
  const emojiRegex = /\p{Emoji_Presentation}|\p{Extended_Pictographic}/gu;
  const parts = text.split(emojiRegex);
  const emojis = text.match(emojiRegex) || [];

  return parts.map((part, i) => {
    const cleanPart = part.replace(/^[\uFE0F\uFE0E]/, "");
    const emoji = emojis[i];
    const hex = emoji ? emojiToHex(emoji) : "";

    return (
      <React.Fragment key={i}>
        {cleanPart}
        {emoji && (
          <img
            src={`${EMOJI_CDN_BASE_URL}/${hex}.png`}
            alt={emoji}
            style={{
              width: "1.25em",
              height: "1.25em",
              margin: "0 2px",
              verticalAlign: "-0.25em",
              pointerEvents: "none",
              display: "inline",
            }}
            loading="lazy"
          />
        )}
      </React.Fragment>
    );
  });
}

// Simple helper to parse emojis in strings or arrays of elements
function parseEmojis(node: React.ReactNode): React.ReactNode {
  if (node === null || node === undefined) return node;
  if (typeof node === "string") return withAppleEmojis(node);
  if (Array.isArray(node)) return node.map((child, i) => <React.Fragment key={i}>{parseEmojis(child)}</React.Fragment>);
  return node;
}

/**
 * Preprocesses message text to normalize template formatting and format according to Markdown standard:
 * 1. Normalizes WhatsApp template bullets (•) to Markdown bullet format (-).
 * 2. Normalizes template bold (*text*) to Markdown bold (**text**).
 * 3. Linkifies plain text URLs by wrapping in <...>.
 */
function preprocessText(text: string): string {
  if (!text) return "";

  // 1. WhatsApp bullet character (•) → Markdown list marker (-)
  let processed = text.replace(/^([ \t]*)•\s+/gm, "$1- ");

  // 2. WhatsApp bold (*text*) → Markdown bold (**text**)
  processed = processed.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "**$1**");

  // 3. Auto-linkify raw URLs (wrap in <...> for standard Markdown parsing)
  processed = processed.replace(/(?<![<(])\b(https?:\/\/[^\s<>"\)]+)/g, "<$1>");

  return processed;
}

interface Props {
  text: string;
  lastMessagePreview?: boolean;
}

export default function MessageContent({ text, lastMessagePreview = false }: Props) {
  const preprocessed = preprocessText(text || "");

  // Dynamic layout styling based on whether we need a single-line preview (grid cell) or multi-line bubble
  const displayStyle = lastMessagePreview ? "inline" : "block";

  // Reusable heading
  const Heading = ({ children }: { children?: React.ReactNode }) => (
    <strong style={{
      display: displayStyle,
      marginBottom: lastMessagePreview ? 0 : "2px",
      whiteSpace: lastMessagePreview ? "nowrap" : "pre-wrap"
    }}>
      {parseEmojis(children)}
    </strong>
  );

  const COMPONENTS: React.ComponentProps<typeof ReactMarkdown>["components"] = {
    h1: Heading,
    h2: Heading,
    h3: Heading,
    h4: Heading,
    h5: Heading,
    h6: Heading,

    p: ({ children }: { children?: React.ReactNode }) => (
      <span style={{
        display: displayStyle,
        marginBottom: lastMessagePreview ? 0 : "4px",
        whiteSpace: lastMessagePreview ? "nowrap" : "pre-wrap"
      }}>
        {parseEmojis(children)}
      </span>
    ),

    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <em style={{
        display: displayStyle,
        margin: lastMessagePreview ? 0 : "2px 0",
        whiteSpace: lastMessagePreview ? "nowrap" : "pre-wrap"
      }}>
        {parseEmojis(children)}
      </em>
    ),

    ul: ({ children }: { children?: React.ReactNode }) => (
      lastMessagePreview ? <span style={{ display: "inline" }}>{children}</span> :
      <ul style={{ margin: "2px 0", paddingLeft: "20px", listStyleType: "disc", whiteSpace: "normal" }}>{children}</ul>
    ),

    ol: ({ children }: { children?: React.ReactNode }) => (
      lastMessagePreview ? <span style={{ display: "inline" }}>{children}</span> :
      <ol style={{ margin: "2px 0", paddingLeft: "20px", listStyleType: "decimal", whiteSpace: "normal" }}>{children}</ol>
    ),

    li: ({ children }: { children?: React.ReactNode }) => (
      lastMessagePreview ? <span style={{ display: "inline", marginRight: "6px" }}> • {parseEmojis(children)}</span> :
      <li style={{
        marginBottom: "2px",
        lineHeight: "1.5",
        whiteSpace: "pre-wrap"
      }}>
        {parseEmojis(children)}
      </li>
    ),

    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#039be5", textDecoration: "underline", display: "inline" }}
      >
        {parseEmojis(children) || href}
      </a>
    ),

    strong: ({ children }: { children?: React.ReactNode }) => <strong style={{ display: "inline" }}>{parseEmojis(children)}</strong>,
    em: ({ children }: { children?: React.ReactNode }) => <em style={{ display: "inline" }}>{parseEmojis(children)}</em>,
    hr: () => lastMessagePreview ? null : <div style={{ marginBottom: "6px" }} />,
  };

  return (
    <span style={{
      display: displayStyle,
      lineHeight: "1.5",
      wordBreak: "break-word",
      whiteSpace: lastMessagePreview ? "nowrap" : "normal"
    }}>
      <ReactMarkdown components={COMPONENTS}>{preprocessed}</ReactMarkdown>
    </span>
  );
}
